import { create } from 'zustand';
import { uuidV4 } from '../crypto/random.js';

export interface ShortcutBinding {
  id: string;
  chord: string;
  handler: (event: KeyboardEvent) => void;
  description?: string;
  scope?: string;
}

export interface ShortcutsState {
  bindings: ShortcutBinding[];
  register: (input: { chord: string; handler: (e: KeyboardEvent) => void; description?: string; scope?: string }) => () => void;
  getRegistered: () => ShortcutBinding[];
  clearScope: (scope: string) => void;
  handleEvent: (event: KeyboardEvent) => void;
}

function normalizeChord(input: string): string {
  return input
    .toLowerCase()
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean)
    .sort((a, b) => {
      const order: Record<string, number> = { mod: 0, cmd: 0, ctrl: 0, alt: 1, shift: 2 };
      return (order[a] ?? 9) - (order[b] ?? 9);
    })
    .join('+');
}

function chordFromEvent(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push('mod');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  const key = (e.key ?? '').toLowerCase();
  if (!key) return parts.join('+');
  if (key !== 'meta' && key !== 'control' && key !== 'alt' && key !== 'shift') parts.push(key);
  return parts.join('+');
}

export const useShortcutsStore = create<ShortcutsState>((set, get) => ({
  bindings: [],
  register: ({ chord, handler, description, scope }) => {
    const id = uuidV4();
    const binding: ShortcutBinding = {
      id,
      chord: normalizeChord(chord),
      handler,
      ...(description !== undefined ? { description } : {}),
      ...(scope !== undefined ? { scope } : {})
    };
    set((s) => ({ bindings: [...s.bindings, binding] }));
    return () => set((s) => ({ bindings: s.bindings.filter((b) => b.id !== id) }));
  },
  getRegistered: () => get().bindings,
  clearScope: (scope) => set((s) => ({ bindings: s.bindings.filter((b) => b.scope !== scope) })),
  handleEvent: (event) => {
    const chord = chordFromEvent(event);
    const matching = get().bindings.filter((b) => b.chord === chord);
    for (const binding of matching) binding.handler(event);
  }
}));

let listenerInstalled = false;
if (typeof window !== 'undefined' && !listenerInstalled) {
  listenerInstalled = true;
  window.addEventListener('keydown', (e) => useShortcutsStore.getState().handleEvent(e));
}
